import {
  RUNNER_STATS_BUCKET_MS,
  RUNNER_STATS_RANGE_MS,
  type RunnerStatsHistoryWire,
  type RunnerStatsRange,
} from '@developerz/domain';
import { OFFLINE_STATUSES } from './fleet-machines';
import type { UtilBox } from './fleetUtilization';

// A machine's stats history as the details panel draws it (#6565, epic #6552), pure.
// The read is `GET /v1/runners/:id/stats?range=` (#6558): each bucket's maximum, ONLY
// the buckets that hold a sample, oldest first. Nothing here invents a reading.
//
// A GAP IS A BREAK (owner ruling G4). The api omits an empty bucket instead of sending a
// zero, so a gap is two neighbours further apart than one bucket width
// (`RUNNER_STATS_BUCKET_MS`: 1 min / 15 min / 2 h); a null reading (`disk_pct` on a
// machine not yet on the disk-total build) breaks that series alone. Points are placed by
// their TIME across the window, never by index: index spacing closes every gap.
//
// "PARTIAL" IS MEASURED AGAINST THE FIRST BUCKET. `collecting_since_iso` is the oldest
// sample INSIDE the range (`bucketRunnerStats`), so a fully covered range still starts a
// few seconds after `now - range`; only a first sample later than one bucket width into
// the window reads as partial, or every full hour would say "59 of 60 minutes".
//
// "HISTORY PAUSED" NEEDS A MACHINE THAT IS CHECKING IN (owner ruling G20): a stale newest
// sample on an offline / not responding machine is the machine's own silence, already
// said by its status, and its chart simply ends at the last reading.

/** The three series the panel charts, by their wire field. */
export type HistorySeries = 'cpu_pct' | 'mem_pct' | 'disk_pct';

/** One drawn point: `x` across the window and `y` up the axis, both 0..1. */
export type HistoryPoint = { readonly x: number; readonly y: number };

/** The newest sample may be this old before the panel reads "History paused". */
export const HISTORY_PAUSED_AFTER_MS = 10 * 60_000;

const clamp01 = (n: number): number => Math.min(1, Math.max(0, n));

/**
 * The drawn lines of one series: each inner array is one unbroken run of real points.
 * A neighbour more than one bucket width away, or a null reading, starts a new run. A
 * reading above `yMax` clips at the top (the CPU axis stops at 300%).
 */
export function historySegments(
  history: Pick<RunnerStatsHistoryWire, 'range' | 'points'>,
  series: HistorySeries,
  yMax: number,
  nowMs: number,
): HistoryPoint[][] {
  const width = RUNNER_STATS_BUCKET_MS[history.range];
  const span = RUNNER_STATS_RANGE_MS[history.range];
  const start = nowMs - span;
  const out: HistoryPoint[][] = [];
  let run: HistoryPoint[] = [];
  let prevMs: number | null = null;
  for (const p of history.points) {
    const ms = Date.parse(p.t);
    const value = p[series];
    const joined = prevMs !== null && ms - prevMs <= width;
    if (value === null || !joined) {
      if (run.length > 0) out.push(run);
      run = [];
    }
    if (value !== null) run.push({ x: clamp01((ms - start) / span), y: clamp01(value / yMax) });
    prevMs = ms;
  }
  if (run.length > 0) out.push(run);
  return out;
}

/** How a partial range counts what it has: minutes of an hour, hours of a day, days of a week. */
export type PartialUnit = 'minutes' | 'hours' | 'days';

export type PartialRange = {
  /** The oldest sample inside the range: "Collecting since <time>". */
  readonly since_iso: string;
  /** Whole units collected so far. */
  readonly done: number;
  readonly total: number;
  readonly unit: PartialUnit;
};

const UNIT: Readonly<
  Record<
    RunnerStatsRange,
    { readonly unit: PartialUnit; readonly ms: number; readonly total: number }
  >
> = {
  '1h': { unit: 'minutes', ms: 60_000, total: 60 },
  '24h': { unit: 'hours', ms: 3_600_000, total: 24 },
  '7d': { unit: 'days', ms: 86_400_000, total: 7 },
};

/**
 * The partial line, or null when the range is covered from its first bucket (or holds no
 * sample at all, which the panel says as "No readings yet." instead).
 */
export function partialRange(
  history: Pick<RunnerStatsHistoryWire, 'range' | 'collecting_since_iso'>,
  nowMs: number,
): PartialRange | null {
  const sinceIso = history.collecting_since_iso;
  if (sinceIso === null) return null;
  const since = Date.parse(sinceIso);
  const start = nowMs - RUNNER_STATS_RANGE_MS[history.range];
  if (since - start <= RUNNER_STATS_BUCKET_MS[history.range]) return null;
  const u = UNIT[history.range];
  return {
    since_iso: sinceIso,
    done: Math.max(0, Math.floor((nowMs - since) / u.ms)),
    total: u.total,
    unit: u.unit,
  };
}

/**
 * "History paused, last reading <when>": the newest sample is older than ten minutes
 * while the machine itself is still checking in. Null otherwise, and for an empty range.
 */
export function historyPaused(
  history: Pick<RunnerStatsHistoryWire, 'last_sample_iso'>,
  status: UtilBox['verdict']['status'],
  nowMs: number,
): { readonly last_iso: string } | null {
  const last = history.last_sample_iso;
  if (last === null || OFFLINE_STATUSES.includes(status)) return null;
  return nowMs - Date.parse(last) > HISTORY_PAUSED_AFTER_MS ? { last_iso: last } : null;
}

/** The latest real reading of one series, for the chart's accessible summary; null when none. */
export function latestReading(
  history: Pick<RunnerStatsHistoryWire, 'points'>,
  series: HistorySeries,
): number | null {
  for (let i = history.points.length - 1; i >= 0; i--) {
    const v = history.points[i]?.[series];
    if (v !== null && v !== undefined) return v;
  }
  return null;
}
